// Human in the loop — big "Done" history fixture (00-ds-09 §2, D148, lists at
// scale). Deterministic ~420 closed items so the Done history can actually page
// and search client-side. Real data comes from the server (§8); this only
// exists so the demo can show "Done in the last 30 days" over a large set.

import { generate, pick, int, fullName, PAYERS, MOCK_NOW } from '@/components/lists';
import type { DoneItem } from './types';

const KINDS = [
  'Ask the patient',
  'Give ELVA access',
  'Make a call',
  'Ask the dentist',
  'Approve a payment',
] as const;

const ACTORS = [
  'Ava Thompson (Lone Star Billing)',
  'Ryan Cole (Lone Star Billing)',
  'Maria Delgado (Lone Star Billing)',
  'Dana Fisher (Lone Star Billing)',
] as const;

const PORTALS = ['Aetna', 'MetLife', 'Cigna', 'UnitedHealthcare', 'Guardian', 'DentaQuest'] as const;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function label(d: Date): string {
  return `${MONTHS[d.getMonth()] ?? 'Jan'} ${String(d.getDate())}, ${String(d.getFullYear())}`;
}

/** A closed-item title + outcome, by kind, using the seeded rng. */
function byKind(r: () => number, kind: (typeof KINDS)[number]): { title: string; outcome: string } {
  const name = fullName(r);
  const payer = pick(r, PAYERS);
  const portal = pick(r, PORTALS);
  switch (kind) {
    case 'Ask the patient':
      return {
        title: `${name}’s ${payer} member ID`,
        outcome: pick(r, [
          'ELVA checked coverage and sent the claim.',
          'ELVA verified eligibility and posted the estimate.',
          'Closed without an answer: patient did not reply.',
        ]),
      };
    case 'Give ELVA access':
      return {
        title: `${portal} portal one-time code`,
        outcome: pick(r, [
          'ELVA downloaded the EOB and posted it.',
          'ELVA pulled the remittance and reconciled the deposit.',
          'ELVA signed in and checked the claim status.',
        ]),
      };
    case 'Make a call':
      return {
        title: `${payer} about ${name}’s claim`,
        outcome: pick(r, [
          `${payer} reprocessed the claim.`,
          `${payer} confirmed the payment is on the way.`,
          'Closed without an answer: line disconnected.',
        ]),
      };
    case 'Ask the dentist':
      return {
        title: pick(r, [
          `Reason for the extraction on #${String(int(r, 1, 32))}`,
          `Narrative for the crown on #${String(int(r, 1, 32))}`,
          `Pre-op condition on #${String(int(r, 1, 32))}`,
        ]),
        outcome: 'ELVA wrote the narrative and sent the claim.',
      };
    case 'Approve a payment':
      return {
        title: `${payer} overpayment refund for ${name}`,
        outcome: pick(r, [
          'ELVA issued the refund and noted the ledger.',
          'ELVA applied the credit to the next visit.',
        ]),
      };
  }
}

/**
 * ~420 closed items weighted toward the recent past so "Done in the last 30
 * days" is a large, pageable set, with a long tail across earlier months for
 * "Search all time". Newest first.
 */
export const DONE_HISTORY: DoneItem[] = generate<DoneItem>(420, 0x5a1e, (r, i) => {
  const kind = pick(r, KINDS);
  const { title, outcome } = byKind(r, kind);
  // 70% within the last 30 days; the rest spread back ~5 months.
  const daysBack = r() < 0.7 ? int(r, 0, 29) : int(r, 30, 150);
  const d = new Date(MOCK_NOW);
  d.setDate(d.getDate() - daysBack);
  return {
    id: `dh-${String(i)}`,
    kindLabel: kind,
    title,
    by: pick(r, ACTORS),
    outcome,
    dateIso: d.toISOString().slice(0, 10),
    dateLabel: label(d),
  };
}).sort((a, b) => (a.dateIso && b.dateIso ? b.dateIso.localeCompare(a.dateIso) : 0));

/** ISO cutoff (inclusive) for the last 30 days, measured from MOCK_NOW. */
export function last30Cutoff(): string {
  const d = new Date(MOCK_NOW);
  d.setDate(d.getDate() - 30);
  return d.toISOString().slice(0, 10);
}

/** How many closed items fall in the last 30 days. */
export function doneInLast30(rows: DoneItem[]): number {
  const cutoff = last30Cutoff();
  return rows.filter((x) => (x.dateIso ?? '9999') >= cutoff).length;
}
